/**
 * Re:Nav-96 API Service Client
 *
 * Centralized API client for all backend communication.
 * Handles authentication, request/response interceptors, and typed API methods.
 *
 * @example
 * import { api } from '@/services/api';
 *
 * // Login
 * const response = await api.auth.login({ email: 'user@example.com', password: 'pass' });
 *
 * // Fetch photos
 * const photos = await api.photos.list({ page: 1, limit: 20 });
 */

import type {
  ApiResponse,
  ApiErrorResponse,
  ApiError,
  PaginatedResponse,
  PaginationParams,
  // Auth types
  User,
  AuthResponse,
  RefreshTokenResponse,
  LoginCredentials,
  RegisterData,
  UserProfile,
  UserProfileUpdate,
  PasswordChangeData,
  VerificationRequest,
  VerificationSubmit,
  // Event types
  EventRegistration,
  RegistrationFormData,
  RegistrationResponse,
  VerificationInput,
  VerificationResponse,
  TicketLookup,
  TicketLookupResponse,
  ResendVerificationRequest,
  RegistrationStats,
  // Message board types
  Folder,
  Group,
  Message,
  // Photo types
  Photo,
  // Survey types
  SurveyForm,
  SurveyResponse,
  // Dynamic content types
  DynamicContent,
} from '@/types';

// ============================================================================
// Configuration
// ============================================================================

/**
 * API base URL - uses Vite proxy in development, can be overridden via env
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Token storage keys
 */
const TOKEN_KEY = 'renav96_token';
const REFRESH_TOKEN_KEY = 'renav96_refresh_token';

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get the stored authentication token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get the stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store authentication tokens
 */
export function setTokens(token: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * Clear stored tokens (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if user has a stored token
 */
export function hasToken(): boolean {
  return !!getToken();
}

// ============================================================================
// HTTP Client
// ============================================================================

/**
 * Custom API error class with additional context
 */
export class ApiClientError extends Error {
  public status: number;
  public code?: string;
  public field?: string;
  public details?: Record<string, unknown>;
  public errors?: ApiError[];

  constructor(error: ApiError, status: number, errors?: ApiError[]) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = error.code;
    this.field = error.field;
    this.details = error.details;
    this.errors = errors;
  }
}

/**
 * Request options for the HTTP client
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | string[] | undefined>;
  body?: unknown;
  skipAuth?: boolean;
}

/**
 * Build URL with query parameters
 */
function buildUrl(path: string, params?: RequestOptions['params']): string {
  const url = new URL(path, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          // Handle arrays by appending multiple params with same key
          value.forEach((v) => url.searchParams.append(key, String(v)));
        } else {
          url.searchParams.append(key, String(value));
        }
      }
    });
  }

  return url.pathname + url.search;
}

/**
 * Flag to prevent multiple simultaneous token refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempt to refresh the authentication token
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const data = (await response.json()) as ApiResponse<RefreshTokenResponse>;
    if (data.success) {
      setTokens(data.data.token);
      return data.data.token;
    }

    return null;
  } catch {
    clearTokens();
    return null;
  }
}

/**
 * Core HTTP request function with authentication and error handling
 */
async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, body, skipAuth = false, ...fetchOptions } = options;

  // Build URL with query params
  const url = buildUrl(`${API_BASE_URL}${path}`, params);

  // Build headers
  const headers = new Headers(fetchOptions.headers);

  if (!headers.has('Content-Type') && body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  // Add auth token if available and not skipped
  if (!skipAuth) {
    const token = getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Make request
  let response = await fetch(url, {
    method,
    ...fetchOptions,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Handle 401 - attempt token refresh
  if (response.status === 401 && !skipAuth) {
    // Prevent multiple simultaneous refresh attempts
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken();
    }

    const newToken = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (newToken) {
      // Retry request with new token
      headers.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(url, {
        method,
        ...fetchOptions,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    }
  }

  // Parse response
  const contentType = response.headers.get('Content-Type');
  let data: unknown;

  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else if (contentType?.includes('application/pdf')) {
    // Return blob for PDF responses
    return (await response.blob()) as T;
  } else {
    data = await response.text();
  }

  // Handle errors
  if (!response.ok) {
    const errorResponse = data as ApiErrorResponse;
    throw new ApiClientError(
      errorResponse.error || { message: 'An unexpected error occurred' },
      response.status,
      errorResponse.errors
    );
  }

  // Return data from successful response
  const successResponse = data as ApiResponse<T>;
  if (successResponse.success !== undefined) {
    return successResponse.data;
  }

  return data as T;
}

// ============================================================================
// HTTP Methods
// ============================================================================

/**
 * Bypass the auto-unwrap in `request<T>` for endpoints whose response
 * envelope carries extra siblings to `data` (e.g. `pagination`) that the
 * unwrap would discard. Returns the parsed JSON body as-is.
 */
async function rawGet<T>(
  path: string,
  params?: Record<string, string | number | boolean | string[] | undefined>,
): Promise<T> {
  const token = getToken();
  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
      else url.searchParams.append(key, String(value));
    });
  }
  const res = await fetch(url.pathname + url.search, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errBody = body as ApiErrorResponse;
    throw new ApiClientError(
      errBody.error || { message: 'An unexpected error occurred' },
      res.status,
      errBody.errors,
    );
  }
  return body as T;
}

const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
};

// ============================================================================
// API Service Methods
// ============================================================================

/**
 * Authentication API methods
 */
export const authApi = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await http.post<AuthResponse>('/auth/login', credentials, {
      skipAuth: true,
    });
    setTokens(response.token, response.refreshToken);
    return response;
  },

  /**
   * Register a new account
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await http.post<AuthResponse>('/auth/register', data, {
      skipAuth: true,
    });
    setTokens(response.token, response.refreshToken);
    return response;
  },

  /**
   * Logout - clears tokens
   */
  async logout(): Promise<void> {
    try {
      await http.post('/auth/logout');
    } finally {
      clearTokens();
    }
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<RefreshTokenResponse | null> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const response = await http.post<RefreshTokenResponse>(
      '/auth/refresh',
      { refreshToken },
      { skipAuth: true }
    );
    setTokens(response.token);
    return response;
  },

  /**
   * Get current authenticated user
   */
  async getMe(): Promise<User> {
    // Backend wraps the response as `{ user: {...} }`; unwrap to match the
    // shape the rest of the app already expects (the email/password login
    // flow's AuthResponse returns the user at the root).
    const data = await http.get<{ user: User } | User>('/auth/me');
    return 'user' in data && data.user ? (data as { user: User }).user : (data as User);
  },

  /**
   * Get the current user's scoped memberships (institution / chapter / branch).
   */
  async getMyMemberships(): Promise<{
    memberships: Array<{ scopeType: 'institution' | 'chapter' | 'branch'; scopeId: string; role: 'admin' | 'contributor' | 'read_only' }>;
  }> {
    return http.get('/auth/me/memberships');
  },

  /**
   * Get available OAuth providers (gated by backend env config).
   */
  async getProviders(): Promise<{
    providers: string[];
    available: { google: boolean; facebook: boolean; microsoft: boolean };
  }> {
    return http.get('/auth/providers', { skipAuth: true });
  },

  /**
   * Request email/phone verification code
   */
  async requestVerification(data: VerificationRequest): Promise<{ message: string }> {
    return http.post<{ message: string }>('/auth/verify/request', data);
  },

  /**
   * Submit verification code
   */
  async submitVerification(data: VerificationSubmit): Promise<{ verified: boolean }> {
    return http.post<{ verified: boolean }>('/auth/verify/submit', data);
  },

  /**
   * Change password
   */
  async changePassword(data: PasswordChangeData): Promise<{ message: string }> {
    return http.post<{ message: string }>('/auth/change-password', data);
  },

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return http.post<{ message: string }>('/auth/forgot-password', { email }, { skipAuth: true });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return http.post<{ message: string }>(
      '/auth/reset-password',
      { token, newPassword },
      { skipAuth: true }
    );
  },

  /**
   * Get Google OAuth URL
   */
  getGoogleAuthUrl(): string {
    return `${API_BASE_URL}/auth/google`;
  },

  /**
   * Get Facebook OAuth URL
   */
  getFacebookAuthUrl(): string {
    return `${API_BASE_URL}/auth/facebook`;
  },

  /**
   * Get Microsoft OAuth URL
   */
  getMicrosoftAuthUrl(): string {
    return `${API_BASE_URL}/auth/microsoft`;
  },
};

/**
 * User API methods
 */
export const usersApi = {
  /**
   * Get user profile by ID
   */
  async getProfile(userId: string): Promise<UserProfile> {
    return http.get<UserProfile>(`/users/${userId}`);
  },

  /**
   * Update current user's profile
   */
  async updateProfile(data: UserProfileUpdate): Promise<UserProfile> {
    return http.patch<UserProfile>('/users/me', data);
  },

  /**
   * Upload avatar image
   */
  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/users/me/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new ApiClientError(error.error, response.status);
    }

    const data = (await response.json()) as ApiResponse<{ avatarUrl: string }>;
    return data.data;
  },

  /**
   * List all users (admin only).
   *
   * Hits /api/admin/users — the legacy paginated full-fidelity surface.
   * That endpoint returns `{ users, pagination }`; we reshape to the
   * `PaginatedResponse<User>` shape the rest of the app expects.
   */
  async list(params?: PaginationParams): Promise<PaginatedResponse<User>> {
    const body = await rawGet<{ users: User[]; pagination: PaginatedResponse<User>['pagination'] }>(
      '/admin/users',
      params,
    );
    return {
      data: body.users,
      pagination: body.pagination,
    } as PaginatedResponse<User>;
  },

  /**
   * Update user role (admin only)
   */
  async updateRole(userId: string, role: string): Promise<User> {
    return http.patch<User>(`/admin/users/${userId}/role`, { role });
  },
};

/**
 * Event Registration API methods
 */
export const registrationApi = {
  /**
   * Create a new event registration
   */
  async create(data: RegistrationFormData): Promise<RegistrationResponse> {
    return http.post<RegistrationResponse>('/registration', data, { skipAuth: true });
  },

  /**
   * Verify registration with code
   */
  async verify(data: VerificationInput): Promise<VerificationResponse> {
    return http.post<VerificationResponse>('/registration/verify', data, { skipAuth: true });
  },

  /**
   * Resend verification code
   */
  async resendVerification(data: ResendVerificationRequest): Promise<{ message: string }> {
    return http.post<{ message: string }>('/registration/resend', data, { skipAuth: true });
  },

  /**
   * Look up existing registration
   */
  async lookup(data: TicketLookup): Promise<TicketLookupResponse> {
    return http.post<TicketLookupResponse>('/registration/lookup', data, { skipAuth: true });
  },

  /**
   * Get registration by ticket number
   */
  async getByTicket(ticketNumber: string): Promise<EventRegistration> {
    return http.get<EventRegistration>(`/registration/${ticketNumber}`, { skipAuth: true });
  },

  /**
   * Download ticket PDF
   */
  async downloadTicket(ticketNumber: string): Promise<Blob> {
    return http.get<Blob>(`/registration/${ticketNumber}/ticket`);
  },

  /**
   * Get registration statistics (admin only)
   */
  async getStats(): Promise<RegistrationStats> {
    return http.get<RegistrationStats>('/registration/stats');
  },

  /**
   * List all registrations (admin only)
   */
  async list(params?: PaginationParams): Promise<PaginatedResponse<EventRegistration>> {
    return http.get<PaginatedResponse<EventRegistration>>('/registration', { params });
  },

  /**
   * Export registrations to CSV (admin only)
   */
  async exportCsv(): Promise<Blob> {
    return http.get<Blob>('/registration/export');
  },
};

/**
 * Folders API methods
 */
export const foldersApi = {
  /**
   * List all folders with their groups
   */
  async list(): Promise<Folder[]> {
    return http.get<Folder[]>('/groups/folders');
  },

  /**
   * Get folder by ID
   */
  async get(folderId: string): Promise<Folder> {
    return http.get<Folder>(`/groups/folders/${folderId}`);
  },

  /**
   * Create folder (admin only)
   */
  async create(data: { name: string; description?: string }): Promise<Folder> {
    return http.post<Folder>('/groups/folders', data);
  },

  /**
   * Update folder (admin only)
   */
  async update(
    folderId: string,
    data: { name?: string; description?: string; sortOrder?: number }
  ): Promise<Folder> {
    return http.patch<Folder>(`/groups/folders/${folderId}`, data);
  },

  /**
   * Delete folder (admin only)
   */
  async delete(folderId: string): Promise<void> {
    return http.delete(`/groups/folders/${folderId}`);
  },
};

/**
 * Groups API methods
 */
export const groupsApi = {
  /**
   * List all groups
   */
  async list(params?: { folderId?: string }): Promise<Group[]> {
    return http.get<Group[]>('/groups', { params });
  },

  /**
   * Get group by ID
   */
  async get(groupId: string): Promise<Group> {
    return http.get<Group>(`/groups/${groupId}`);
  },

  /**
   * Create group (admin only)
   */
  async create(data: {
    /** Either folderId or chapterId is required. When chapterId is
     *  supplied the backend resolves the chapter's General folder. */
    folderId?: string;
    chapterId?: string;
    name: string;
    description?: string;
    allowMemberPosts?: boolean;
    requireApproval?: boolean;
    aiModeration?: boolean;
    visibility?: GroupVisibility;
    joinPolicy?: GroupJoinPolicy;
  }): Promise<Group> {
    return http.post<Group>('/groups', data);
  },

  /**
   * Update group (admin only)
   */
  async update(
    groupId: string,
    data: {
      name?: string;
      description?: string;
      allowMemberPosts?: boolean;
      requireApproval?: boolean;
      aiModeration?: boolean;
      sortOrder?: number;
      visibility?: GroupVisibility;
      joinPolicy?: GroupJoinPolicy;
    }
  ): Promise<Group> {
    return http.patch<Group>(`/groups/${groupId}`, data);
  },

  /**
   * Delete group (admin only)
   */
  async delete(groupId: string): Promise<void> {
    return http.delete(`/groups/${groupId}`);
  },

  /**
   * Join a group
   */
  async join(groupId: string): Promise<{ message: string }> {
    return http.post<{ message: string }>(`/groups/${groupId}/join`);
  },

  /**
   * Leave a group
   */
  async leave(groupId: string): Promise<{ message: string }> {
    return http.post<{ message: string }>(`/groups/${groupId}/leave`);
  },

  /**
   * Update digest preferences for a group
   */
  async updateDigestPreference(
    groupId: string,
    frequency: 'daily' | 'weekly' | 'none'
  ): Promise<{ message: string }> {
    return http.patch<{ message: string }>(`/groups/${groupId}/digest`, { frequency });
  },
};

/**
 * Messages API methods
 */
export const messagesApi = {
  /**
   * Get messages for a group
   */
  async list(
    groupId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<{ messages: Message[]; nextCursor?: string }> {
    // Backend returns `{ success, data, pagination: { count, limit, cursor, hasMore } }`.
    // The shared HTTP client auto-unwraps the envelope to just `data`, which
    // discards the cursor. Use rawGet and reshape to the legacy
    // `{ messages, nextCursor }` shape the page expects.
    const body = await rawGet<{
      data: Array<Message & { id?: string }>;
      pagination?: { cursor?: string | null; hasMore?: boolean };
    }>(`/groups/${groupId}/messages`, params);
    // Backend serialises rowKey as `id`; map back so the Message type
    // (and existing consumers that key by rowKey) keep working.
    const messages: Message[] = (Array.isArray(body.data) ? body.data : []).map((m) => ({
      ...m,
      rowKey: m.rowKey ?? m.id ?? '',
      partitionKey: m.partitionKey ?? groupId,
    }));
    return {
      messages,
      nextCursor: body.pagination?.cursor ?? undefined,
    };
  },

  /**
   * Get a specific message
   */
  async get(groupId: string, rowKey: string): Promise<Message> {
    return http.get<Message>(`/groups/${groupId}/messages/${rowKey}`);
  },

  /**
   * Post a new message
   */
  async create(groupId: string, data: { content: string; photoIds?: string[] }): Promise<Message> {
    return http.post<Message>(`/groups/${groupId}/messages`, data);
  },

  /**
   * Delete a message
   */
  async delete(groupId: string, rowKey: string): Promise<void> {
    return http.delete(`/groups/${groupId}/messages/${rowKey}`);
  },

  /**
   * Get pending messages for moderation (admin only)
   */
  async getPending(groupId: string): Promise<Message[]> {
    return http.get<Message[]>(`/groups/${groupId}/messages/pending`);
  },

  /**
   * Approve a pending message (admin only)
   */
  async approve(groupId: string, rowKey: string): Promise<Message> {
    return http.post<Message>(`/groups/${groupId}/messages/${rowKey}/approve`);
  },

  /**
   * Reject a pending message (admin only)
   */
  async reject(groupId: string, rowKey: string): Promise<void> {
    return http.post(`/groups/${groupId}/messages/${rowKey}/reject`);
  },

  /**
   * Get latest messages across all groups (for marquee)
   */
  async getLatest(params?: { limit?: number }): Promise<Message[]> {
    return http.get<Message[]>('/messages/latest', { params });
  },
};

/**
 * Photos API methods
 */
export const photosApi = {
  /**
   * List approved photos
   *
   * NOTE: bypasses the auto-unwrap in the shared HTTP client because the
   * server response carries `pagination` alongside `data` — unwrapping
   * would discard it and the page needs both.
   */
  async list(
    params?: PaginationParams & { tags?: string[] }
  ): Promise<PaginatedResponse<Photo>> {
    return rawGet<PaginatedResponse<Photo>>('/photos', params);
  },

  /**
   * Get photo by ID
   */
  async get(photoId: string): Promise<Photo> {
    return http.get<Photo>(`/photos/${photoId}`);
  },

  /**
   * Upload a photo
   */
  async upload(
    file: File,
    data?: { tags?: string[] }
  ): Promise<Photo> {
    const formData = new FormData();
    formData.append('photo', file);
    if (data?.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/photos`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new ApiClientError(error.error, response.status);
    }

    const responseData = (await response.json()) as ApiResponse<Photo>;
    return responseData.data;
  },

  /**
   * Delete a photo
   */
  async delete(photoId: string): Promise<void> {
    return http.delete(`/photos/${photoId}`);
  },

  /**
   * Add tags to a photo
   */
  async addTags(photoId: string, tags: string[]): Promise<Photo> {
    return http.post<Photo>(`/photos/${photoId}/tags`, { tags });
  },

  /**
   * Remove a tag from a photo
   */
  async removeTag(photoId: string, tag: string): Promise<Photo> {
    return http.delete<Photo>(`/photos/${photoId}/tags/${encodeURIComponent(tag)}`);
  },

  /**
   * Get pending photos for approval (admin only)
   *
   * Same bypass-auto-unwrap rationale as `list()` above.
   */
  async getPending(params?: PaginationParams): Promise<PaginatedResponse<Photo>> {
    return rawGet<PaginatedResponse<Photo>>('/photos/pending', params);
  },

  /**
   * Approve a photo (admin only)
   */
  async approve(photoId: string): Promise<Photo> {
    return http.post<Photo>(`/photos/${photoId}/approve`);
  },

  /**
   * Reject a photo (admin only)
   */
  async reject(photoId: string): Promise<void> {
    return http.post(`/photos/${photoId}/reject`);
  },

  /**
   * Get random approved photos (for carousel)
   */
  async getRandom(params?: { limit?: number }): Promise<Photo[]> {
    return http.get<Photo[]>('/photos/random', { params });
  },

  /**
   * Get available tags
   */
  async getTags(): Promise<string[]> {
    return http.get<string[]>('/photos/tags');
  },

  /**
   * Get photo statistics (admin only)
   */
  async getStats(): Promise<{ pending: number; approved: number; rejected: number; total: number }> {
    return http.get<{ pending: number; approved: number; rejected: number; total: number }>('/photos/stats');
  },
};

/**
 * Surveys API methods
 */
export const surveysApi = {
  /**
   * List all surveys
   */
  async list(params?: PaginationParams): Promise<PaginatedResponse<SurveyForm>> {
    return http.get<PaginatedResponse<SurveyForm>>('/surveys', { params });
  },

  /**
   * Get survey by ID
   */
  async get(surveyId: string): Promise<SurveyForm> {
    return http.get<SurveyForm>(`/surveys/${surveyId}`);
  },

  /**
   * Create a new survey (admin only)
   */
  async create(
    data: Pick<SurveyForm, 'title' | 'description' | 'formSchema' | 'groupId' | 'resultsPublic'>
  ): Promise<SurveyForm> {
    return http.post<SurveyForm>('/surveys', data);
  },

  /**
   * Update a survey (admin only)
   */
  async update(
    surveyId: string,
    data: Partial<Pick<SurveyForm, 'title' | 'description' | 'formSchema' | 'resultsPublic' | 'isActive'>>
  ): Promise<SurveyForm> {
    return http.patch<SurveyForm>(`/surveys/${surveyId}`, data);
  },

  /**
   * Delete a survey (admin only)
   */
  async delete(surveyId: string): Promise<void> {
    return http.delete(`/surveys/${surveyId}`);
  },

  /**
   * Submit a survey response
   */
  async submit(surveyId: string, responses: Record<string, unknown>): Promise<SurveyResponse> {
    return http.post<SurveyResponse>(`/surveys/${surveyId}/responses`, { responses });
  },

  /**
   * Get survey responses (admin or if public)
   */
  async getResponses(
    surveyId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<SurveyResponse>> {
    return http.get<PaginatedResponse<SurveyResponse>>(
      `/surveys/${surveyId}/responses`,
      { params }
    );
  },

  /**
   * Export survey responses to CSV (admin only)
   */
  async exportResponses(surveyId: string): Promise<Blob> {
    return http.get<Blob>(`/surveys/${surveyId}/export`);
  },
};

/**
 * Dynamic Content API methods
 */
export const contentApi = {
  /**
   * Get content by section key
   */
  async get(sectionKey: string): Promise<DynamicContent> {
    return http.get<DynamicContent>(`/content/${sectionKey}`, { skipAuth: true });
  },

  /**
   * Get multiple content sections
   */
  async getMultiple(sectionKeys: string[]): Promise<Record<string, DynamicContent>> {
    return http.post<Record<string, DynamicContent>>(
      '/content/batch',
      { sectionKeys },
      { skipAuth: true }
    );
  },

  /**
   * List all content sections (admin only)
   */
  async list(): Promise<DynamicContent[]> {
    return http.get<DynamicContent[]>('/content');
  },

  /**
   * Update content section (admin only)
   */
  async update(
    sectionKey: string,
    data: { title?: string; htmlContent?: string; isActive?: boolean }
  ): Promise<DynamicContent> {
    return http.put<DynamicContent>(`/content/${sectionKey}`, data);
  },

  /**
   * Create a new content section (admin only)
   */
  async create(
    data: { sectionKey: string; title?: string; htmlContent?: string }
  ): Promise<DynamicContent> {
    return http.post<DynamicContent>('/content', data);
  },
};

/**
 * Documents API methods
 */
/**
 * spec-130 — group visibility + join policy types.
 */
export type GroupVisibility = 'listed' | 'unlisted';
export type GroupJoinPolicy = 'inherit_chapter' | 'open' | 'request' | 'closed';
export type GroupJoinAction = 'none' | 'join' | 'request' | 'closed' | 'already_member';

export interface GroupMemberRow {
  userId: string;
  groupId: string;
  role: 'member' | 'admin';
  displayName?: string;
  email?: string | null;
  avatarUrl?: string | null;
  joinedAt: string;
}

export interface GroupJoinRequestRow {
  id: string;
  userId: string;
  groupId: string;
  state: 'pending' | 'approved' | 'rejected';
  requestNote?: string | null;
  decidedAt?: string | null;
  createdAt: string;
  user?: { id: string; email: string | null; displayName: string; avatarUrl: string | null } | null;
}

export const groupMembersApi = {
  async list(groupId: string): Promise<GroupMemberRow[]> {
    const d = await http.get<{ data: GroupMemberRow[] }>(`/groups/${groupId}/members`);
    return Array.isArray((d as unknown as { data?: unknown }).data)
      ? (d as { data: GroupMemberRow[] }).data
      : (d as unknown as GroupMemberRow[]);
  },
  async add(groupId: string, input: { userId: string; role?: 'member' | 'admin' }): Promise<GroupMemberRow> {
    const d = await http.post<{ membership: GroupMemberRow }>(`/groups/${groupId}/members`, input);
    return (d as unknown as { membership?: GroupMemberRow }).membership ?? (d as unknown as GroupMemberRow);
  },
  async setRole(groupId: string, userId: string, role: 'member' | 'admin'): Promise<GroupMemberRow> {
    const d = await http.patch<{ membership: GroupMemberRow }>(`/groups/${groupId}/members/${userId}`, { role });
    return (d as unknown as { membership?: GroupMemberRow }).membership ?? (d as unknown as GroupMemberRow);
  },
  async remove(groupId: string, userId: string): Promise<void> {
    return http.delete(`/groups/${groupId}/members/${userId}`);
  },
  async join(groupId: string, note?: string): Promise<{ alreadyMember?: boolean; success?: boolean; request?: GroupJoinRequestRow }> {
    return http.post(`/groups/${groupId}/join`, note ? { note } : {});
  },
  async listJoinRequests(groupId: string, state?: 'pending' | 'approved' | 'rejected'): Promise<GroupJoinRequestRow[]> {
    const d = await http.get<{ data: GroupJoinRequestRow[] }>(`/groups/${groupId}/join-requests`, state ? { params: { state } } : undefined);
    return Array.isArray((d as unknown as { data?: unknown }).data)
      ? (d as { data: GroupJoinRequestRow[] }).data
      : (d as unknown as GroupJoinRequestRow[]);
  },
  async approveJoinRequest(groupId: string, id: string, note?: string): Promise<void> {
    await http.post(`/groups/${groupId}/join-requests/${id}/approve`, note ? { note } : {});
  },
  async rejectJoinRequest(groupId: string, id: string, note?: string): Promise<void> {
    await http.post(`/groups/${groupId}/join-requests/${id}/reject`, note ? { note } : {});
  },
  async myRequests(): Promise<Array<{ id: string; groupId: string; state: 'pending' | 'approved' | 'rejected'; createdAt: string; decidedAt: string | null }>> {
    const d = await http.get<{ data: Array<{ id: string; groupId: string; state: 'pending'|'approved'|'rejected'; createdAt: string; decidedAt: string | null }> }>(`/me/group-join-requests`);
    return Array.isArray((d as unknown as { data?: unknown }).data)
      ? (d as { data: Array<{ id: string; groupId: string; state: 'pending'|'approved'|'rejected'; createdAt: string; decidedAt: string | null }> }).data
      : (d as unknown as Array<{ id: string; groupId: string; state: 'pending'|'approved'|'rejected'; createdAt: string; decidedAt: string | null }>);
  },
};

/**
 * Group-scoped Resources API — per-group pinned links + files.
 *
 * spec-130 phase 9 unified Documents into Resources with a `kind`
 * discriminator. `kind='link'` rows use `url`; `kind='file'` rows
 * carry `blobUrl` + filename/mimeType/fileSize.
 */
export type GroupResourceKind = 'link' | 'file';

export interface GroupResource {
  id: string;
  groupId: string;
  kind: GroupResourceKind;
  title: string;
  url: string;
  blobUrl?: string | null;
  filename?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  description?: string | null;
  category?: string | null;
  createdById: string;
  createdByName?: string;
  createdAt: string;
}

/**
 * spec-140 — group-scoped TaskList + Task.
 */
export type GroupTaskStatus = 'open' | 'in_progress' | 'blocked' | 'done';

export interface GroupTask {
  id: string;
  taskListId: string;
  title: string;
  description: string | null;
  status: GroupTaskStatus;
  priority: number | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  dueDate: string | null;
  createdById: string;
  completedById: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GroupTaskList {
  id: string;
  groupId: string | null;
  name: string;
  sortOrder: number;
  createdAt: string;
  tasks: GroupTask[];
}

export const groupTasksApi = {
  async listLists(groupId: string): Promise<GroupTaskList[]> {
    const d = await http.get<{ data: GroupTaskList[] }>(`/groups/${groupId}/task-lists`);
    return Array.isArray((d as unknown as { data?: unknown }).data)
      ? (d as { data: GroupTaskList[] }).data
      : (d as unknown as GroupTaskList[]);
  },
  async getList(listId: string): Promise<GroupTaskList & { groupName?: string }> {
    const d = await http.get<{ data: GroupTaskList & { groupName?: string } }>(`/task-lists/${listId}`);
    return (d as unknown as { data?: GroupTaskList & { groupName?: string } }).data ?? (d as unknown as GroupTaskList & { groupName?: string });
  },
  async createList(groupId: string, name: string): Promise<GroupTaskList> {
    const d = await http.post<{ data: GroupTaskList }>(`/groups/${groupId}/task-lists`, { name });
    return (d as unknown as { data?: GroupTaskList }).data ?? (d as unknown as GroupTaskList);
  },
  async renameList(listId: string, name: string): Promise<void> {
    await http.patch(`/task-lists/${listId}`, { name });
  },
  async deleteList(listId: string): Promise<void> {
    await http.delete(`/task-lists/${listId}`);
  },
  async createTask(listId: string, input: {
    title: string;
    description?: string;
    assigneeUserId?: string | null;
    dueDate?: string | null;
    priority?: number;
  }): Promise<GroupTask> {
    const d = await http.post<{ data: GroupTask }>(`/task-lists/${listId}/tasks`, input);
    return (d as unknown as { data?: GroupTask }).data ?? (d as unknown as GroupTask);
  },
  async updateTask(taskId: string, patch: Partial<{
    title: string;
    description: string | null;
    status: GroupTaskStatus;
    assigneeUserId: string | null;
    dueDate: string | null;
    priority: number;
  }>): Promise<GroupTask> {
    const d = await http.patch<{ data: GroupTask }>(`/tasks/${taskId}`, patch);
    return (d as unknown as { data?: GroupTask }).data ?? (d as unknown as GroupTask);
  },
  async deleteTask(taskId: string): Promise<void> {
    await http.delete(`/tasks/${taskId}`);
  },
};

/**
 * spec-130 phase 10 — cross-group merged streams.
 */
export interface MergedMessageRow {
  id: string;
  groupId: string;
  groupName: string | null;
  authorId: string;
  authorName: string;
  content: string;
  contentType: string;
  isAdminOnly: boolean;
  status: string;
  createdAt: string;
}

export interface MergedResourceRow extends GroupResource {
  groupName: string | null;
}

export const meStreamsApi = {
  async messages(opts?: { groupIds?: string[]; limit?: number }): Promise<{ data: MergedMessageRow[]; groups: Array<{ id: string; name: string }> }> {
    const params: Record<string, string | number> = {};
    if (opts?.groupIds && opts.groupIds.length > 0) params.groupIds = opts.groupIds.join(',');
    if (opts?.limit) params.limit = opts.limit;
    // rawGet keeps {data, groups} together — http.get's auto-unwrap
    // would drop the `groups` sibling.
    return rawGet(`/me/messages`, params);
  },
  async resources(opts?: { groupIds?: string[]; kind?: GroupResourceKind }): Promise<{ data: MergedResourceRow[]; groups: Array<{ id: string; name: string }> }> {
    const params: Record<string, string> = {};
    if (opts?.groupIds && opts.groupIds.length > 0) params.groupIds = opts.groupIds.join(',');
    if (opts?.kind) params.kind = opts.kind;
    return rawGet(`/me/resources`, params);
  },
};

export const resourcesApi = {
  async list(groupId: string, kind?: GroupResourceKind): Promise<GroupResource[]> {
    const data = await http.get<{ data: GroupResource[] }>(`/groups/${groupId}/resources`, kind ? { params: { kind } } : undefined);
    return Array.isArray((data as unknown as { data?: unknown }).data)
      ? (data as { data: GroupResource[] }).data
      : (data as unknown as GroupResource[]);
  },
  async createLink(
    groupId: string,
    input: { title: string; url: string; description?: string; category?: string },
  ): Promise<GroupResource> {
    const data = await http.post<{ data: GroupResource }>(`/groups/${groupId}/resources`, input);
    return (data as unknown as { data?: GroupResource }).data ?? (data as unknown as GroupResource);
  },
  // Back-compat alias for existing GroupResourcesPanel callers.
  async create(
    groupId: string,
    input: { title: string; url: string; description?: string; category?: string },
  ): Promise<GroupResource> {
    return resourcesApi.createLink(groupId, input);
  },
  async uploadFile(
    groupId: string,
    file: File,
    options?: { title?: string; description?: string },
  ): Promise<GroupResource> {
    const formData = new FormData();
    formData.append('document', file);
    if (options?.title) formData.append('title', options.title);
    if (options?.description) formData.append('description', options.description);

    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/resources/files`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      const message = typeof err.error === 'string'
        ? err.error
        : err.error?.message ?? err.message ?? 'Upload failed';
      throw new ApiClientError({ message }, res.status);
    }
    const body = (await res.json()) as { data: GroupResource };
    return body.data;
  },
  async remove(groupId: string, resourceId: string): Promise<void> {
    return http.delete(`/groups/${groupId}/resources/${resourceId}`);
  },
};

/**
 * spec-130 phase 9 — documentsApi is replaced by resourcesApi with
 * `kind='file'`. This shim keeps existing GroupMessages / DocumentList
 * / DocumentUploader callers working while we migrate them; new code
 * should use resourcesApi directly.
 */
export const documentsApi = {
  async list(groupId: string): Promise<PaginatedResponse<{
    id: string;
    filename: string;
    description?: string;
    mimeType?: string;
    fileSize?: number;
    blobUrl?: string;
    uploaderId: string;
    uploaderName?: string;
    createdAt: string;
  }>> {
    const rows = await resourcesApi.list(groupId, 'file');
    return {
      data: rows.map((r) => ({
        id: r.id,
        filename: r.filename ?? r.title,
        description: r.description ?? undefined,
        mimeType: r.mimeType ?? undefined,
        fileSize: r.fileSize ?? undefined,
        blobUrl: r.blobUrl ?? undefined,
        uploaderId: r.createdById,
        uploaderName: r.createdByName,
        createdAt: r.createdAt,
      })),
      pagination: { page: 1, limit: rows.length, total: rows.length, totalPages: 1, hasNext: false, hasPrev: false },
    };
  },
  async upload(
    groupId: string,
    file: File,
    description?: string,
  ): Promise<{ id: string; filename: string; blobUrl: string }> {
    const r = await resourcesApi.uploadFile(groupId, file, { description });
    return { id: r.id, filename: r.filename ?? file.name, blobUrl: r.blobUrl ?? '' };
  },
  async download(_groupId: string, documentId: string): Promise<Blob> {
    // The unified Resources API hands back blobUrl on list rows; the
    // caller can keep that and use it as the download href. This shim
    // retains the legacy callsite shape by fetching the list and
    // re-reading the matching row.
    const list = await resourcesApi.list(_groupId, 'file');
    const found = list.find((r) => r.id === documentId);
    if (!found || !found.blobUrl) {
      throw new ApiClientError({ message: 'Document not found' }, 404);
    }
    const res = await fetch(found.blobUrl);
    if (!res.ok) {
      throw new ApiClientError({ message: `Download failed (${res.status})` }, res.status);
    }
    return res.blob();
  },
  async delete(groupId: string, documentId: string): Promise<void> {
    return resourcesApi.remove(groupId, documentId);
  },
};

// ============================================================================
// User search (admin-gated lookup)
// ============================================================================

export interface UserSearchResult {
  id: string;
  email: string | null;
  displayName: string;
  avatarUrl: string | null;
}

export const usersSearchApi = {
  async search(q: string, limit = 10): Promise<UserSearchResult[]> {
    if (q.trim().length < 2) return [];
    const data = await http.get<{ users: UserSearchResult[] }>('/users/search', {
      params: { q, limit },
    });
    return data.users;
  },
};

// ============================================================================
// Multitenant types
// ============================================================================

export type ScopeType = 'institution' | 'chapter' | 'branch';
export type ScopeRole = 'admin' | 'contributor' | 'read_only';

export interface ChapterSummary {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  batchYear?: number;
  location?: string | null;
  landingPageId?: string | null;
  institutionId: string;
}

export interface BranchSummary {
  id: string;
  slug: string;
  name: string;
  displayName?: string | null;
  kind: string;
  landingPageId?: string | null;
  chapterId: string;
}

export interface Invitation {
  id: string;
  code: string;
  scopeType: ScopeType;
  scopeId: string;
  role: ScopeRole;
  inviterDisplay: string;
  expiresAt: string;
  maxUses?: number | null;
  useCount: number;
  revokedAt?: string | null;
  isActive: boolean;
}

export interface InvitationLookup {
  code: string;
  scopeType: ScopeType;
  scopeId: string;
  scope: { displayName: string; slug: string } | null;
  role: ScopeRole;
  inviterDisplay: string;
  expiresAt: string;
  suggestedBranchIds: string[];
}

export interface AcceptInvitationResponse {
  ok: true;
  alreadyMember: boolean;
  membership: { scopeType: ScopeType; scopeId: string; role: ScopeRole };
  joinRequest: { id: string; branchId: string; state: string } | null;
}

export interface BranchJoinRequest {
  id: string;
  userId: string;
  user: { id: string; email: string | null; displayName: string; avatarUrl: string | null } | null;
  branchId: string;
  requestedRole: ScopeRole | null;
  state: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

// ============================================================================
// Chapters & Branches
// ============================================================================

export interface ResolvedScope {
  institution: { id: string; slug: string; name: string; displayName: string; defaultChapterSlug?: string };
  chapter: ChapterSummary | null;
  branch: BranchSummary | null;
}

export const scopeApi = {
  /** Resolve slugs (institution / chapter? / branch?) to ids in one call. */
  async resolve(institution = 'navarasam', chapter?: string, branch?: string): Promise<ResolvedScope> {
    return http.get<ResolvedScope>('/scope/resolve', {
      params: { institution, chapter, branch },
      skipAuth: true,
    });
  },
};

export const chaptersApi = {
  async list(institutionSlug = 'navarasam'): Promise<ChapterSummary[]> {
    const data = await http.get<{ chapters: ChapterSummary[] }>('/chapters', {
      params: { institutionSlug },
    });
    return data.chapters;
  },
  async get(idOrSlug: string): Promise<ChapterSummary> {
    const data = await http.get<{ chapter: ChapterSummary }>(`/chapters/${encodeURIComponent(idOrSlug)}`);
    return data.chapter;
  },
  async listBranches(chapterIdOrSlug: string): Promise<BranchSummary[]> {
    const data = await http.get<{ branches: BranchSummary[] }>(
      `/chapters/${encodeURIComponent(chapterIdOrSlug)}/branches`,
    );
    return data.branches;
  },
  async create(input: {
    institutionSlug?: string;
    slug: string;
    name: string;
    displayName?: string;
    batchYear?: number;
    location?: string;
  }): Promise<ChapterSummary> {
    const data = await http.post<{ chapter: ChapterSummary }>(`/chapters`, input);
    return data.chapter;
  },
  async update(id: string, patch: Partial<{
    name: string;
    displayName: string;
    batchYear: number;
    location: string;
  }>): Promise<ChapterSummary> {
    const data = await http.put<{ chapter: ChapterSummary }>(`/chapters/${encodeURIComponent(id)}`, patch);
    return data.chapter;
  },
  async remove(id: string): Promise<{ ok: true; deletedChapterId: string }> {
    return http.delete<{ ok: true; deletedChapterId: string }>(`/chapters/${encodeURIComponent(id)}`);
  },
};

export interface InstitutionRow {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  defaultChapterSlug?: string | null;
  brandSettingsJson?: string | null;
  createdAt: string;
}

export const institutionsApi = {
  async list(): Promise<InstitutionRow[]> {
    const data = await http.get<{ institutions: InstitutionRow[] }>('/institutions');
    return data.institutions;
  },
  async get(idOrSlug: string): Promise<InstitutionRow> {
    const data = await http.get<{ institution: InstitutionRow }>(`/institutions/${encodeURIComponent(idOrSlug)}`);
    return data.institution;
  },
  async update(id: string, patch: Partial<{
    displayName: string;
    defaultChapterSlug: string | null;
    brandSettingsJson: string | null;
  }>): Promise<InstitutionRow> {
    const data = await http.put<{ institution: InstitutionRow }>(`/institutions/${encodeURIComponent(id)}`, patch);
    return data.institution;
  },
};

export const adminUsersApi = {
  async promote(userId: string, institutionSlug = 'navarasam'): Promise<{ ok: true; updated?: boolean; created?: boolean }> {
    return http.post<{ ok: true; updated?: boolean; created?: boolean }>(`/admin/users/${encodeURIComponent(userId)}/promote`, { institutionSlug });
  },
};

// ============================================================================
// Invitations
// ============================================================================

export const invitationsApi = {
  async list(scopeType: ScopeType, scopeId: string): Promise<Invitation[]> {
    const data = await http.get<{ invitations: Invitation[] }>('/invitations', {
      params: { scopeType, scopeId },
    });
    return data.invitations;
  },
  async create(input: {
    scopeType: ScopeType;
    scopeId: string;
    role?: ScopeRole;
    expiresInDays?: number;
    maxUses?: number;
    suggestedBranchIds?: string[];
  }): Promise<{ invitation: Invitation }> {
    return http.post<{ invitation: Invitation }>('/invitations', input);
  },
  async get(code: string): Promise<InvitationLookup> {
    return http.get<InvitationLookup>(`/invitations/${encodeURIComponent(code)}`, {
      skipAuth: true,
    });
  },
  async accept(code: string, body: { chosenBranchId?: string } = {}): Promise<AcceptInvitationResponse> {
    return http.post<AcceptInvitationResponse>(`/invitations/${encodeURIComponent(code)}/accept`, body);
  },
  async revoke(id: string): Promise<{ ok: true }> {
    return http.delete<{ ok: true }>(`/invitations/${encodeURIComponent(id)}`);
  },
};

// ============================================================================
// Pages & PageParts
// ============================================================================

export type PagePartKind = 'html' | 'gallery';

export interface HtmlPartConfig {
  html: string;
  container?: 'narrow' | 'wide' | 'full';
}

export interface GalleryFilter {
  galleryId?: string;
  folderId?: string;
  folderPath?: string;
  tags?: string[];
  mediaKind?: 'photo' | 'video';
}

export interface GalleryPartConfig {
  filter: GalleryFilter;
  layout?: 'grid' | 'masonry';
  maxItems?: number;
  showFolderNav?: boolean;
}

export type PagePartConfig = HtmlPartConfig | GalleryPartConfig;

export interface PagePart {
  id: string;
  kind: PagePartKind;
  sortOrder: number;
  config: PagePartConfig;
}

export interface PageSummary {
  id: string;
  ownerScopeType: ScopeType;
  ownerScopeId: string;
  slug: string;
  title: string;
  isLandingPage: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PageDetail extends PageSummary {
  parts: PagePart[];
}

export const pagesApi = {
  async list(scopeType: ScopeType, scopeId: string): Promise<PageSummary[]> {
    const data = await http.get<{ pages: PageSummary[] }>('/pages', { params: { scopeType, scopeId } });
    return data.pages;
  },
  async getById(id: string): Promise<PageDetail> {
    const data = await http.get<{ page: PageDetail }>(`/pages/${encodeURIComponent(id)}`);
    return data.page;
  },
  async getBySlug(scopeType: ScopeType, scopeId: string, slug: string): Promise<PageDetail> {
    const data = await http.get<{ page: PageDetail }>(`/pages/${encodeURIComponent(slug)}`, {
      params: { scopeType, scopeId },
    });
    return data.page;
  },
  async create(input: {
    scopeType: ScopeType;
    scopeId: string;
    slug: string;
    title: string;
    isLandingPage?: boolean;
    isPublic?: boolean;
    parts?: Array<{ kind: PagePartKind; config: PagePartConfig; sortOrder?: number }>;
  }): Promise<PageDetail> {
    const data = await http.post<{ page: PageDetail }>('/pages', input);
    return data.page;
  },
  async update(id: string, patch: Partial<Pick<PageSummary, 'slug' | 'title' | 'isLandingPage' | 'isPublic'>>): Promise<PageDetail> {
    const data = await http.put<{ page: PageDetail }>(`/pages/${encodeURIComponent(id)}`, patch);
    return data.page;
  },
  async remove(id: string): Promise<{ ok: true }> {
    return http.delete<{ ok: true }>(`/pages/${encodeURIComponent(id)}`);
  },
  async replaceParts(
    id: string,
    parts: Array<{ kind: PagePartKind; config: PagePartConfig; sortOrder?: number }>,
  ): Promise<PageDetail> {
    const data = await http.put<{ page: PageDetail }>(`/pages/${encodeURIComponent(id)}/parts`, { parts });
    return data.page;
  },
  async appendPart(
    id: string,
    part: { kind: PagePartKind; config: PagePartConfig; sortOrder?: number },
  ): Promise<PagePart> {
    const data = await http.post<{ part: PagePart }>(`/pages/${encodeURIComponent(id)}/parts`, part);
    return data.part;
  },
  async updatePart(partId: string, patch: { sortOrder?: number; config?: PagePartConfig }): Promise<PagePart> {
    const data = await http.put<{ part: PagePart }>(`/parts/${encodeURIComponent(partId)}`, patch);
    return data.part;
  },
  async deletePart(partId: string): Promise<{ ok: true }> {
    return http.delete<{ ok: true }>(`/parts/${encodeURIComponent(partId)}`);
  },
  async uploadPartImage(partId: string, file: File): Promise<{ url: string; blobName: string }> {
    const form = new FormData();
    form.append('file', file);
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/parts/${encodeURIComponent(partId)}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(body.error ?? body.message ?? `Upload failed (HTTP ${res.status})`);
    }
    return res.json();
  },
  /**
   * Page-scoped image upload — preferred for the inline RichTextEditor
   * since part IDs change on each atomic parts-replace.
   */
  async uploadPageImage(pageId: string, file: File): Promise<{ url: string; blobName: string }> {
    const form = new FormData();
    form.append('file', file);
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/pages/${encodeURIComponent(pageId)}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(body.error ?? body.message ?? `Upload failed (HTTP ${res.status})`);
    }
    return res.json();
  },
  /**
   * Public, no-auth gallery items for a single gallery part on a public
   * page. Returns 404 if the page isn't public or the part isn't a
   * gallery part, so an anonymous probe can't enumerate private content.
   */
  async listPublicPartItems(pageId: string, partId: string): Promise<{ items: import('./api').GalleryItem[]; total: number }> {
    return http.get(
      `/pages/${encodeURIComponent(pageId)}/parts/${encodeURIComponent(partId)}/items`,
      { skipAuth: true },
    );
  },
};

// ============================================================================
// Galleries / Folders / Items / Share links
// ============================================================================

export interface GallerySummary {
  id: string;
  ownerScopeType: ScopeType;
  ownerScopeId: string;
}

export interface GalleryFolder {
  id: string;
  parentFolderId: string | null;
  name: string;
  path: string;
}

export interface GalleryItem {
  id: string;
  mediaKind: 'photo' | 'video';
  blobUrl: string;
  thumbnailUrl: string | null;
  posterBlobUrl: string | null;
  durationSec: number | null;
  galleryFolderId: string | null;
  caption: string | null;
  tags: string[];
  width: number | null;
  height: number | null;
  status: string;
  createdAt: string;
  uploaderId?: string;
  uploaderName?: string | null;
  /** Present on /api/share/:token responses — SAS-time-limited URL. */
  url?: string;
}

export interface GalleryShareLink {
  id: string;
  token: string;
  url: string;
  filter: {
    folderId?: string;
    folderPath?: string;
    tags?: string[];
    from?: string;
    to?: string;
  } | null;
  expiresAt: string;
  revokedAt: string | null;
  usedCount: number;
  maxUses: number | null;
  isActive: boolean;
}

export const galleriesApi = {
  async get(scopeType: ScopeType, scopeId: string): Promise<{ gallery: GallerySummary; folders: GalleryFolder[]; totalItems: number }> {
    return http.get(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}`);
  },
  async listItems(
    scopeType: ScopeType,
    scopeId: string,
    opts: { folderId?: string; folderPath?: string; tag?: string; page?: number; pageSize?: number; status?: 'pending' | 'approved' | 'rejected' } = {},
  ): Promise<{ items: GalleryItem[]; total: number; page: number; pageSize: number }> {
    return http.get(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/items`, { params: opts });
  },
  /** Admin: approve or reject a pending gallery item. */
  async moderateItem(
    scopeType: ScopeType,
    scopeId: string,
    itemId: string,
    status: 'approved' | 'rejected',
  ): Promise<{ item: GalleryItem }> {
    return http.post(
      `/galleries/${scopeType}/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}/moderate`,
      { status },
    );
  },
  async createFolder(scopeType: ScopeType, scopeId: string, name: string, parentFolderId?: string): Promise<GalleryFolder> {
    const data = await http.post<{ folder: GalleryFolder }>(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/folders`, {
      name,
      parentFolderId,
    });
    return data.folder;
  },
  async renameFolder(scopeType: ScopeType, scopeId: string, folderId: string, name: string): Promise<GalleryFolder> {
    const data = await http.put<{ folder: GalleryFolder }>(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/folders/${encodeURIComponent(folderId)}`, { name });
    return data.folder;
  },
  async deleteFolder(scopeType: ScopeType, scopeId: string, folderId: string): Promise<{ ok: true }> {
    return http.delete(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/folders/${encodeURIComponent(folderId)}`);
  },
  async uploadItems(
    scopeType: ScopeType,
    scopeId: string,
    files: File[],
    opts: { folderId?: string; tags?: string[]; caption?: string } = {},
  ): Promise<{ items: GalleryItem[] }> {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    if (opts.folderId) form.append('folderId', opts.folderId);
    if (opts.tags && opts.tags.length > 0) form.append('tags', opts.tags.join(','));
    if (opts.caption) form.append('caption', opts.caption);
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/galleries/${scopeType}/${encodeURIComponent(scopeId)}/items`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(body.error ?? body.message ?? `Upload failed (HTTP ${res.status})`);
    }
    return res.json();
  },
  async listShareLinks(scopeType: ScopeType, scopeId: string): Promise<GalleryShareLink[]> {
    const data = await http.get<{ shareLinks: GalleryShareLink[] }>(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/share-links`);
    return data.shareLinks;
  },
  async createShareLink(
    scopeType: ScopeType,
    scopeId: string,
    input: { filter?: GalleryShareLink['filter']; expiresAt: string; maxUses?: number },
  ): Promise<{ id: string; token: string; url: string; expiresAt: string; maxUses: number | null }> {
    return http.post(`/galleries/${scopeType}/${encodeURIComponent(scopeId)}/share-links`, input);
  },
  async revokeShareLink(id: string): Promise<{ ok: true }> {
    return http.delete(`/share-links/${encodeURIComponent(id)}`);
  },
};

export const shareApi = {
  async get(token: string): Promise<{ expiresAt: string; total: number; items: GalleryItem[] }> {
    return http.get(`/share/${encodeURIComponent(token)}`, { skipAuth: true });
  },
};

// ============================================================================
// Projects
// ============================================================================

export type ProjectStatus = 'active' | 'archived' | 'completed';
export type ProjectResourceKind = 'link' | 'video' | 'doc' | 'other';
export type TaskStatus = 'open' | 'in_progress' | 'blocked' | 'done';

export interface Project {
  id: string;
  ownerScopeType: ScopeType;
  ownerScopeId: string;
  slug: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  startsAt: string | null;
  endsAt: string | null;
  landingPageId: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectAuthz {
  isProjectMember: boolean;
  projectRole: ScopeRole | null;
  isScopeAdmin: boolean;
}

export interface ProjectMember {
  id: string;
  userId: string;
  role: ScopeRole;
  createdAt: string;
}

export interface ProjectResource {
  id: string;
  projectId: string;
  title: string;
  url: string;
  kind: ProjectResourceKind;
  addedById: string;
  createdAt: string;
}

export interface ProjectFolder {
  id: string;
  parentFolderId: string | null;
  name: string;
  path: string;
}

export interface ProjectDocument {
  id: string;
  projectId: string;
  folderId: string | null;
  blobUrl: string;
  filename: string;
  mimeType: string | null;
  fileSize: number | null;
  uploadedById: string;
  createdAt: string;
}

export interface ProjectTask {
  id: string;
  title: string;
  description: string | null;
  assigneeUserId: string | null;
  dueDate: string | null;
  status: TaskStatus;
  priority: number | null;
  createdById: string;
  completedAt: string | null;
  createdAt: string;
}

export interface ProjectTaskList {
  id: string;
  name: string;
  sortOrder: number;
  tasks: ProjectTask[];
}

export const projectsApi = {
  async list(scopeType: ScopeType, scopeId: string): Promise<Project[]> {
    const data = await http.get<{ projects: Project[] }>('/projects', { params: { scopeType, scopeId } });
    return data.projects;
  },
  async getById(id: string): Promise<{ project: Project; authz: ProjectAuthz }> {
    return http.get<{ project: Project; authz: ProjectAuthz }>(`/projects/${encodeURIComponent(id)}`);
  },
  async create(input: {
    scopeType: ScopeType;
    scopeId: string;
    slug: string;
    name: string;
    description?: string;
    startsAt?: string;
    endsAt?: string;
  }): Promise<Project> {
    const data = await http.post<{ project: Project }>('/projects', input);
    return data.project;
  },
  async update(id: string, patch: Partial<Pick<Project, 'name' | 'description' | 'slug' | 'status' | 'startsAt' | 'endsAt'>>): Promise<Project> {
    const data = await http.put<{ project: Project }>(`/projects/${encodeURIComponent(id)}`, patch);
    return data.project;
  },
  async archive(id: string): Promise<Project> {
    const data = await http.post<{ project: Project }>(`/projects/${encodeURIComponent(id)}/archive`, {});
    return data.project;
  },

  async listMembers(id: string): Promise<ProjectMember[]> {
    const data = await http.get<{ members: ProjectMember[] }>(`/projects/${encodeURIComponent(id)}/members`);
    return data.members;
  },
  async addMember(id: string, userId: string, role: ScopeRole = 'contributor'): Promise<ProjectMember> {
    const data = await http.post<{ member: ProjectMember }>(`/projects/${encodeURIComponent(id)}/members`, { userId, role });
    return data.member;
  },
  async updateMember(id: string, memberId: string, role: ScopeRole): Promise<ProjectMember> {
    const data = await http.put<{ member: ProjectMember }>(`/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`, { role });
    return data.member;
  },
  async removeMember(id: string, memberId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`);
  },

  async listResources(id: string): Promise<ProjectResource[]> {
    const data = await http.get<{ resources: ProjectResource[] }>(`/projects/${encodeURIComponent(id)}/resources`);
    return data.resources;
  },
  async addResource(id: string, body: { title: string; url: string; kind?: ProjectResourceKind }): Promise<ProjectResource> {
    const data = await http.post<{ resource: ProjectResource }>(`/projects/${encodeURIComponent(id)}/resources`, body);
    return data.resource;
  },
  async removeResource(id: string, resourceId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/resources/${encodeURIComponent(resourceId)}`);
  },

  async listFolders(id: string): Promise<ProjectFolder[]> {
    const data = await http.get<{ folders: ProjectFolder[] }>(`/projects/${encodeURIComponent(id)}/folders`);
    return data.folders;
  },
  async createFolder(id: string, name: string, parentFolderId?: string): Promise<ProjectFolder> {
    const data = await http.post<{ folder: ProjectFolder }>(`/projects/${encodeURIComponent(id)}/folders`, { name, parentFolderId });
    return data.folder;
  },
  async deleteFolder(id: string, folderId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}`);
  },

  async listDocuments(id: string, folderId?: string): Promise<ProjectDocument[]> {
    const data = await http.get<{ documents: ProjectDocument[] }>(`/projects/${encodeURIComponent(id)}/documents`, {
      params: folderId ? { folderId } : undefined,
    });
    return data.documents;
  },
  async uploadDocument(id: string, file: File, folderId?: string): Promise<ProjectDocument> {
    const form = new FormData();
    form.append('file', file);
    if (folderId) form.append('folderId', folderId);
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/projects/${encodeURIComponent(id)}/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Upload failed (HTTP ${res.status})`);
    }
    const data = (await res.json()) as { document: ProjectDocument };
    return data.document;
  },
  async deleteDocument(id: string, documentId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`);
  },

  async listTaskLists(id: string): Promise<ProjectTaskList[]> {
    const data = await http.get<{ taskLists: ProjectTaskList[] }>(`/projects/${encodeURIComponent(id)}/task-lists`);
    return data.taskLists;
  },
  async createTaskList(id: string, name: string): Promise<ProjectTaskList> {
    const data = await http.post<{ taskList: ProjectTaskList }>(`/projects/${encodeURIComponent(id)}/task-lists`, { name });
    return data.taskList;
  },
  async deleteTaskList(id: string, listId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/task-lists/${encodeURIComponent(listId)}`);
  },
  async createTask(id: string, listId: string, body: { title: string; description?: string; assigneeUserId?: string; dueDate?: string; priority?: number; status?: TaskStatus }): Promise<ProjectTask> {
    const data = await http.post<{ task: ProjectTask }>(`/projects/${encodeURIComponent(id)}/task-lists/${encodeURIComponent(listId)}/tasks`, body);
    return data.task;
  },
  async updateTask(id: string, listId: string, taskId: string, patch: Partial<Omit<ProjectTask, 'id' | 'createdById' | 'createdAt' | 'completedAt'>> & { taskListId?: string }): Promise<ProjectTask> {
    const data = await http.put<{ task: ProjectTask }>(`/projects/${encodeURIComponent(id)}/task-lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`, patch);
    return data.task;
  },
  async deleteTask(id: string, listId: string, taskId: string): Promise<{ ok: true }> {
    return http.delete(`/projects/${encodeURIComponent(id)}/task-lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`);
  },
};

// ============================================================================
// Events
// ============================================================================

export type EventRegistrationMode = 'none' | 'open' | 'invitee_only';
export type AttendeeState = 'registered' | 'waitlist' | 'cancelled' | 'checked_in';

export interface AgendaItem {
  time: string;
  title: string;
  description?: string;
}

export interface EventV2 {
  id: string;
  ownerScopeType: ScopeType;
  ownerScopeId: string;
  slug: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  timezone: string | null;
  locationText: string | null;
  agenda: AgendaItem[] | null;
  landingPageId: string | null;
  registrationMode: EventRegistrationMode;
  capacity: number | null;
  createdById: string | null;
  createdAt: string;
}

export interface EventAttendee {
  id: string;
  userId: string;
  state: AttendeeState;
  plusOnes: number;
  notes: unknown;
  registeredAt: string;
}

export const eventsApi = {
  async list(scopeType: ScopeType, scopeId: string): Promise<EventV2[]> {
    const data = await http.get<{ events: EventV2[] }>('/events', { params: { scopeType, scopeId } });
    return data.events;
  },
  async getById(id: string): Promise<EventV2> {
    const data = await http.get<{ event: EventV2 }>(`/events/${encodeURIComponent(id)}`);
    return data.event;
  },
  async getBySlug(scopeType: ScopeType, scopeId: string, slug: string): Promise<EventV2> {
    const data = await http.get<{ event: EventV2 }>(`/events/${encodeURIComponent(slug)}`, {
      params: { scopeType, scopeId },
    });
    return data.event;
  },
  async create(input: {
    scopeType: ScopeType;
    scopeId: string;
    slug: string;
    title: string;
    startsAt: string;
    endsAt?: string;
    timezone?: string;
    locationText?: string;
    agenda?: AgendaItem[];
    registrationMode?: EventRegistrationMode;
    capacity?: number;
  }): Promise<EventV2> {
    const data = await http.post<{ event: EventV2 }>('/events', input);
    return data.event;
  },
  async update(id: string, patch: Partial<Omit<EventV2, 'id' | 'ownerScopeType' | 'ownerScopeId' | 'createdById' | 'createdAt' | 'landingPageId'>>): Promise<EventV2> {
    // Translate Date strings; agenda passed through; null endsAt allowed.
    const data = await http.put<{ event: EventV2 }>(`/events/${encodeURIComponent(id)}`, patch);
    return data.event;
  },
  async remove(id: string): Promise<{ ok: true }> {
    return http.delete(`/events/${encodeURIComponent(id)}`);
  },
  async register(id: string, body: { plusOnes?: number; notes?: string } = {}): Promise<{ attendee: EventAttendee }> {
    return http.post(`/events/${encodeURIComponent(id)}/registration`, body);
  },
  async cancel(id: string): Promise<{ ok: true }> {
    return http.delete(`/events/${encodeURIComponent(id)}/registration`);
  },
  async attendees(id: string): Promise<EventAttendee[]> {
    const data = await http.get<{ attendees: EventAttendee[] }>(`/events/${encodeURIComponent(id)}/attendees`);
    return data.attendees;
  },
};

// ============================================================================
// Branch Join Requests
// ============================================================================

export interface ChapterJoinRequestRow {
  id: string;
  /** Present on admin-side listings; omitted on the caller-side `mine()` response. */
  userId?: string;
  chapterId: string;
  state: 'pending' | 'approved' | 'rejected';
  requestedRole: ScopeRole;
  createdAt: string;
  decidedAt?: string | null;
  requestNote?: string | null;
  user?: { id: string; email: string | null; displayName: string; avatarUrl: string | null } | null;
}

export const chapterJoinRequestsApi = {
  /** Create my own request to join a chapter. Idempotent. */
  async create(chapterId: string, body: { requestedRole?: ScopeRole; note?: string } = {}): Promise<
    | { alreadyMember: true; viaInheritance?: boolean; membership?: { scopeType: ScopeType; scopeId: string; role: ScopeRole } }
    | { request: ChapterJoinRequestRow; duplicate?: boolean }
  > {
    return http.post(`/chapters/${encodeURIComponent(chapterId)}/join-requests`, body);
  },
  /** Admin: list pending (or filtered) join requests for a chapter. */
  async list(chapterId: string, state: 'pending' | 'approved' | 'rejected' = 'pending'): Promise<ChapterJoinRequestRow[]> {
    const data = await http.get<{ requests: ChapterJoinRequestRow[] }>(`/chapters/${encodeURIComponent(chapterId)}/join-requests`, {
      params: { state },
    });
    return data.requests;
  },
  async approve(chapterId: string, id: string, body: { role?: ScopeRole; note?: string } = {}): Promise<{ ok: true }> {
    return http.post(`/chapters/${encodeURIComponent(chapterId)}/join-requests/${encodeURIComponent(id)}/approve`, body);
  },
  async reject(chapterId: string, id: string, body: { note?: string } = {}): Promise<{ ok: true }> {
    return http.post(`/chapters/${encodeURIComponent(chapterId)}/join-requests/${encodeURIComponent(id)}/reject`, body);
  },
  /** Caller-side: list my own chapter join requests (any state). */
  async mine(): Promise<ChapterJoinRequestRow[]> {
    const data = await http.get<{ requests: ChapterJoinRequestRow[] }>('/me/chapter-join-requests');
    return data.requests;
  },
};

export const joinRequestsApi = {
  async list(branchId: string, state: 'pending' | 'approved' | 'rejected' = 'pending'): Promise<BranchJoinRequest[]> {
    const data = await http.get<{ requests: BranchJoinRequest[] }>(
      `/branches/${encodeURIComponent(branchId)}/join-requests`,
      { params: { state } },
    );
    return data.requests;
  },
  async approve(branchId: string, id: string, note?: string): Promise<{ ok: true }> {
    return http.post<{ ok: true }>(
      `/branches/${encodeURIComponent(branchId)}/join-requests/${encodeURIComponent(id)}/approve`,
      note ? { note } : {},
    );
  },
  async reject(branchId: string, id: string, note?: string): Promise<{ ok: true }> {
    return http.post<{ ok: true }>(
      `/branches/${encodeURIComponent(branchId)}/join-requests/${encodeURIComponent(id)}/reject`,
      note ? { note } : {},
    );
  },
};

/**
 * Health check API
 */
export const healthApi = {
  /**
   * Check API health
   */
  async check(): Promise<{ status: string; uptime: number; version: string }> {
    return http.get('/health', { skipAuth: true });
  },
};

// ============================================================================
// Combined API Object
// ============================================================================

/**
 * Combined API object for easy access to all endpoints
 *
 * @example
 * import { api } from '@/services/api';
 *
 * // Use individual API modules
 * await api.auth.login(credentials);
 * await api.photos.list();
 * await api.messages.create(groupId, { content: 'Hello!' });
 */
export const api = {
  auth: authApi,
  users: usersApi,
  registration: registrationApi,
  folders: foldersApi,
  groups: groupsApi,
  messages: messagesApi,
  photos: photosApi,
  surveys: surveysApi,
  content: contentApi,
  documents: documentsApi,
  health: healthApi,
  scope: scopeApi,
  chapters: chaptersApi,
  invitations: invitationsApi,
  joinRequests: joinRequestsApi,
  chapterJoinRequests: chapterJoinRequestsApi,
  pages: pagesApi,
  galleries: galleriesApi,
  share: shareApi,
  events: eventsApi,
  projects: projectsApi,
  usersSearch: usersSearchApi,
} as const;

// Default export for convenience
export default api;
